//-- copyright
// GPE customization (gov.ao)
//
// Display field for the actual_start_date / actual_due_date columns.
// Renders the date as usual, but if the actual date is later than the
// corresponding planned date (start_date / due_date), appends a red
// `(+N)` slippage marker computed at render time. Slippage is NOT stored.
//++

import { DateDisplayField } from 'core-app/shared/components/fields/display/field-types/date-display-field.module';
import { InjectField } from 'core-app/shared/helpers/angular/inject-field.decorator';
import { TimezoneService } from 'core-app/core/datetime/timezone.service';
import { I18nService } from 'core-app/core/i18n/i18n.service';

export class ActualDateDisplayField extends DateDisplayField {
  @InjectField() override timezoneService:TimezoneService;

  @InjectField() I18n:I18nService;

  public override get canOverdue():boolean {
    // Actual dates are not "overdue" - they are a record of what happened.
    return false;
  }

  public override render(element:HTMLElement, displayText:string):void {
    super.render(element, displayText);

    const slippageDays = this.computeSlippageDays();
    if (slippageDays === null) {
      return;
    }

    const badge = document.createElement('span');
    if (slippageDays > 0) {
      // Later than planned -> red (+N)
      badge.classList.add('op-actual-date--slippage');
      badge.textContent = ` (+${slippageDays})`;
      badge.title = this.I18n.t('js.work_packages.actual_dates.slippage_tooltip', {
        days: slippageDays,
        defaultValue: `Slippage of ${slippageDays} day(s) vs. planned`,
      });
    } else {
      // On time or ahead of plan (<= 0) -> green (0) / (-N)
      badge.classList.add('op-actual-date--ahead');
      badge.textContent = ` (${slippageDays})`;
      badge.title = this.I18n.t('js.work_packages.actual_dates.ahead_tooltip', {
        days: Math.abs(slippageDays),
        defaultValue: `${Math.abs(slippageDays)} day(s) ahead of / on plan`,
      });
    }
    element.appendChild(badge);
  }

  /**
   * Returns the number of days the actual date deviates from the planned one
   * (positive number = late). Returns null if no comparison is possible.
   */
  private computeSlippageDays():number|null {
    const plannedAttribute = this.plannedCounterpart();
    if (!plannedAttribute) {
      return null;
    }

    const actual = this.value as string|null;
    const planned = (this.resource as unknown as { [k:string]:string|null })[plannedAttribute];

    if (!actual || !planned) {
      return null;
    }

    const actualDate = new Date(actual);
    const plannedDate = new Date(planned);
    if (Number.isNaN(actualDate.getTime()) || Number.isNaN(plannedDate.getTime())) {
      return null;
    }

    const diffMs = actualDate.getTime() - plannedDate.getTime();
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
  }

  private plannedCounterpart():string|null {
    switch (this.name) {
      case 'actualStartDate':
        return 'startDate';
      case 'actualDueDate':
        return 'dueDate';
      default:
        return null;
    }
  }
}
